import { Component, computed, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { BaseModalComponent } from '@card-software/library';
import { ApiResponse, AuthService } from '@card-software/nexus-utilities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import {
  DYEING_STATION_ASSET_ID,
  ItemConsumption,
  ProductionCardDyeingCycle,
} from '@app/models/dyeing-dashboard/production-card-dyeing-cycle';
import {
  CyclePhase,
  FinalizedItemConsumptionView,
  ItemConsumptionForm,
  ItemConsumptionRowView,
} from '@app/models/dyeing-dashboard/production-card-dyeing-cycle-forms';
import { LotItem } from '@app/models/inventory/requisition';
import { UNIT_OF_MEASURE_LABELS, UnitOfMeasureType } from '@app/models/item/enum/UnitOfMeasureType';
import { ProductRecipeIngredient } from '@app/models/item/product-recipe';
import { DyeingRoles } from '@app/models/roles/roles-group';
import { BatchLotItemProxyService } from '@app/proxies/inventory/batch-lot-item.proxy.service';
import { ItemMasterProxyService } from '@app/proxies/item/item-master.proxy.service';
import { ProductRecipeProxyService } from '@app/proxies/item/product-recipe.proxy.service';
import { ProductionCardDyeingCycleProxyService } from '@app/proxies/production/production-card-dyein-cycle.proxy.service';
import { BatchService } from '@app/services/batch.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { confirmAction } from '@app/utilities/confirmation.util';
import { getConsumptionVariance } from '@app/utilities/consumption-variance.util';

@Component({
  selector: 'app-batch-dyeing-cycle-modal',
  templateUrl: './batch-dyeing-cycle-modal.component.html',
  styleUrl: './batch-dyeing-cycle-modal.component.scss',
})
export class BatchDyeingCycleModalComponent extends BaseModalComponent<ProductionCardDyeingCycle | null> implements OnInit {
  @Input() batchId!: string;
  @Input() batchNumber!: string;
  @Input() cycle: ProductionCardDyeingCycle | null = null;
  @Input() recipeIngredients: ProductRecipeIngredient[] = [];
  @Input() batchLotItems: LotItem[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly dyeingCycleProxyService = inject(ProductionCardDyeingCycleProxyService);
  private readonly batchLotItemProxyService = inject(BatchLotItemProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly productRecipeProxyService = inject(ProductRecipeProxyService);
  private readonly batchService = inject(BatchService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly confirmationService = inject(ConfirmationService);

  private itemDisplayNames = signal<Map<string, string>>(new Map());
  private subRecipeOutputItemCodes = signal<Map<string, string>>(new Map());

  activeCycle = signal<ProductionCardDyeingCycle | null>(null);
  isLoading = signal(0);
  isSavingConsumptions = signal(false);
  isConsuming = signal(false);

  isApprovedControl = new FormControl<boolean>(true, { nonNullable: true });
  private isApproved = toSignal(this.isApprovedControl.valueChanges, { initialValue: this.isApprovedControl.value });
  qaComments = new FormControl<string>('');

  consumptionItemsForm = new FormArray<FormGroup<ItemConsumptionForm>>([]);
  waterUsageControl = new FormControl<number>(0, { nonNullable: true });
  itemToAddControl = new FormControl<string | null>(null);

  private consumptionRows = toSignal(this.consumptionItemsForm.valueChanges.pipe(map(() => this.consumptionItemsForm.getRawValue())), {
    initialValue: this.consumptionItemsForm.getRawValue(),
  });
  private waterUsage = toSignal(this.waterUsageControl.valueChanges, { initialValue: this.waterUsageControl.value });
  private batchItemCodes = signal<string[]>([]);

  private estimatedCycleId = computed(() => {
    const cycle = this.activeCycle();
    return cycle?.isFirstRun && cycle.estimatedWaterUsageInLiters != null ? cycle.id : null;
  });

  hasEstimate = computed(() => this.estimatedCycleId() !== null);

  private consumptionEstimate = toSignal(
    toObservable(this.estimatedCycleId).pipe(
      switchMap((cycleId) =>
        cycleId
          ? this.dyeingCycleProxyService.getConsumptionEstimate(cycleId).pipe(
              map((response) => response?.data ?? null),
              catchError(() => of(null)),
            )
          : of(null),
      ),
    ),
    { initialValue: null },
  );

  consumptionRowViews = computed<ItemConsumptionRowView[]>(() => {
    const rows = this.consumptionRows();
    const estimatedByRow = new Array<number | null>(rows.length).fill(null);

    // Same placement as ApplyEstimatedQuantities in Production.API, which stamps these rows on save.
    for (const estimated of this.consumptionEstimate()?.itemConsumptions ?? []) {
      if (estimated.estimatedQuantity == null) continue;

      let rowIndex = rows.findIndex((row) => row.isAvailable && row.itemCode === estimated.itemCode);
      if (rowIndex < 0) rowIndex = rows.findIndex((row) => row.isAvailable && estimated.substituteItemCodes.includes(row.itemCode));
      if (rowIndex >= 0) estimatedByRow[rowIndex] = (estimatedByRow[rowIndex] ?? 0) + estimated.estimatedQuantity;
    }

    return rows.map((row, index) => ({
      displayName: this.getItemDisplayName(row.itemCode),
      uomLabel: this.getUomLabel(row.uom),
      recipeUomHint: this.getRecipeUomHint(row.recipeUom),
      variance: getConsumptionVariance(estimatedByRow[index], row.quantity),
    }));
  });

  waterUsageVariance = computed(() => getConsumptionVariance(this.activeCycle()?.estimatedWaterUsageInLiters, this.waterUsage()));

  finalizedConsumptions = computed<FinalizedItemConsumptionView[]>(() =>
    (this.activeCycle()?.itemConsumptions ?? []).map((item) => ({
      itemCode: item.itemCode,
      quantity: item.quantity,
      displayName: this.getItemDisplayName(item.itemCode),
      uomLabel: this.getUomLabel(item.uom),
      variance: getConsumptionVariance(item.estimatedQuantity, item.quantity),
    })),
  );

  finalizedWaterUsageVariance = computed(() => {
    const cycle = this.activeCycle();
    return getConsumptionVariance(cycle?.estimatedWaterUsageInLiters, cycle?.waterUsageInLiters);
  });

  cyclePhase = computed<CyclePhase>(() => {
    const cycle = this.activeCycle();
    if (!cycle) return 'start';
    if (!cycle.endTime) return 'running';
    if (!cycle.colorStatusApproval?.approvalDate) return 'awaitingQA';
    if (!cycle.removedTime) return 'unloading';
    return 'done';
  });

  showHardStopButton = computed(() => {
    return this.cyclePhase() === 'running' && AuthService.hasRole(DyeingRoles.DyeingAdmin);
  });

  showQASection = computed(() => this.cyclePhase() === 'awaitingQA' && AuthService.hasRole(DyeingRoles.DyeingQualityAdmin));

  showHardApproveButton = computed(() => {
    if (this.cyclePhase() !== 'unloading') return false;
    const approval = this.activeCycle()?.colorStatusApproval;
    return !!approval && !approval.isApproved && AuthService.hasRole(DyeingRoles.DyeingQualityAdmin);
  });

  showCommentsField = computed(() => !this.isApproved());

  // Same unload guard as UpdateItemConsumptionsAsync in Production.API.
  showConsumptionsSection = computed(() => this.cyclePhase() === 'done' && !this.isConsumptionsFinalized());

  isConsumptionsFinalized = computed(() => {
    return this.activeCycle()?.consumptionsFinalized ?? false;
  });

  selectableItems = computed<SelectItem<string>[]>(() => {
    const alreadyInForm = new Set(this.consumptionRows().map((row) => row.itemCode));
    return this.batchItemCodes()
      .filter((itemCode) => !alreadyInForm.has(itemCode))
      .map((itemCode) => ({ value: itemCode, label: this.getItemDisplayName(itemCode) || itemCode }));
  });

  stationAssetId = DYEING_STATION_ASSET_ID;

  ngOnInit(): void {
    this.batchItemCodes.set([
      ...new Set(this.batchLotItems.filter((lotItem) => (lotItem.netQuantity ?? 0) > 0).map((lotItem) => lotItem.itemCode)),
    ]);

    this.resolveSubRecipeOutputItemCodes(() => {
      if (this.cycle) {
        this.activeCycle.set(this.cycle);
        this.initConsumptionItems(this.cycle);
      }
      this.loadItemDisplayNames();
    });
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  private resolveSubRecipeOutputItemCodes(done: () => void): void {
    const recipeNumbers = [
      ...new Set(
        this.recipeIngredients.flatMap((ing) => [
          ...(!ing.itemCode && ing.productRecipeNumber ? [ing.productRecipeNumber] : []),
          ...(ing.substitutes ?? []).filter((s) => !s.itemCode && s.productRecipeNumber).map((s) => s.productRecipeNumber!),
        ]),
      ),
    ];

    if (recipeNumbers.length === 0) {
      done();
      return;
    }

    this.incrementLoading();
    this.productRecipeProxyService
      .getByNumbers(recipeNumbers)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          const outputItemCodes = new Map<string, string>();
          for (const recipe of response?.data ?? []) {
            if (recipe.number && recipe.outputItemCode) outputItemCodes.set(recipe.number, recipe.outputItemCode);
          }
          this.subRecipeOutputItemCodes.set(outputItemCodes);
          done();
        },
        error: () => done(),
      });
  }

  private resolveItemCode(reference: { itemCode?: string; productRecipeNumber?: string }): string {
    if (reference.itemCode) return reference.itemCode;
    if (reference.productRecipeNumber) return this.subRecipeOutputItemCodes().get(reference.productRecipeNumber) ?? '';
    return '';
  }

  private getIngredientVariantItemCodes(ingredient: ProductRecipeIngredient): string[] {
    const codes = [ingredient, ...(ingredient.substitutes ?? [])]
      .map((reference) => this.resolveItemCode(reference))
      .filter((code) => !!code);
    return [...new Set(codes)];
  }

  private getRecipeItemCodes(): Set<string> {
    return new Set(this.recipeIngredients.flatMap((ingredient) => this.getIngredientVariantItemCodes(ingredient)));
  }

  private getUomLabel(uom: number): string {
    return UNIT_OF_MEASURE_LABELS[uom as UnitOfMeasureType] ?? `uom:${uom}`;
  }

  private getItemDisplayName(itemCode: string): string {
    return this.itemDisplayNames().get(itemCode) ?? '';
  }

  private loadItemDisplayNames(): void {
    const codes = new Set<string>(this.batchItemCodes());
    for (const ingredient of this.recipeIngredients) {
      for (const code of this.getIngredientVariantItemCodes(ingredient)) codes.add(code);
    }
    for (const consumption of this.activeCycle()?.itemConsumptions ?? []) {
      if (consumption.itemCode) codes.add(consumption.itemCode);
    }
    if (codes.size === 0) return;

    this.incrementLoading();
    this.itemMasterProxyService
      .getByItemCodes([...codes])
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          const displayNames = new Map<string, string>();
          for (const item of response?.data ?? []) {
            displayNames.set(item.itemCode, this.localeHelperService.getLocalizedValue(item.itemDisplayName) || item.itemCode);
          }
          this.itemDisplayNames.set(displayNames);
        },
      });
  }

  //#region Consumption Items

  private initConsumptionItems(cycle: ProductionCardDyeingCycle): void {
    this.waterUsageControl.setValue(cycle.waterUsageInLiters ?? 0);
    this.consumptionItemsForm.clear();

    if (cycle.itemConsumptions && cycle.itemConsumptions.length > 0) {
      const recipeItemCodes = this.getRecipeItemCodes();
      cycle.itemConsumptions.forEach((item) =>
        this.pushConsumptionRow(item.itemCode, item.quantity, item.uom, true, recipeItemCodes.has(item.itemCode)),
      );
    } else if (this.recipeIngredients.length > 0) {
      this.seedConsumptionsFromRecipe();
    }
  }

  private seedConsumptionsFromRecipe(): void {
    const available = new Set(this.batchLotItems.map((lotItem) => lotItem.itemCode));

    for (const ingredient of this.recipeIngredients) {
      const availableVariants = this.getIngredientVariantItemCodes(ingredient).filter((code) => available.has(code));

      if (availableVariants.length > 0) {
        availableVariants.forEach((code) => this.pushConsumptionRow(code, 0, UnitOfMeasureType.Kg, true, true, ingredient.uom));
      } else {
        this.pushConsumptionRow(this.resolveItemCode(ingredient), 0, UnitOfMeasureType.Kg, false, true, ingredient.uom);
      }
    }
  }

  private pushConsumptionRow(
    itemCode: string,
    quantity: number,
    uom: number,
    isAvailable: boolean,
    isFromRecipe: boolean,
    recipeUom: number | null = null,
  ): void {
    this.consumptionItemsForm.push(
      this.fb.group<ItemConsumptionForm>({
        itemCode: this.fb.control(itemCode, { nonNullable: true }),
        quantity: this.fb.control({ value: quantity, disabled: !isAvailable }, { nonNullable: true }),
        uom: this.fb.control(uom, { nonNullable: true }),
        isAvailable: this.fb.control(isAvailable, { nonNullable: true }),
        isFromRecipe: this.fb.control(isFromRecipe, { nonNullable: true }),
        recipeUom: this.fb.control(recipeUom),
      }),
    );
  }

  addConsumptionRow(): void {
    const itemCode = this.itemToAddControl.value;
    if (!itemCode) return;

    this.pushConsumptionRow(itemCode, 0, UnitOfMeasureType.Kg, true, false);
    this.itemToAddControl.reset();
  }

  private getRecipeUomHint(recipeUom: number | null): string {
    if (recipeUom === null || recipeUom === UnitOfMeasureType.Kg) return '';
    return this.localeHelperService.translateWithFallback(
      'batchDyeingCycle.recipeUnitHint',
      `The recipe lists this item in ${this.getUomLabel(recipeUom)} — enter the amount consumed in kg.`,
      { uom: this.getUomLabel(recipeUom) },
    );
  }

  removeConsumptionRow(index: number): void {
    this.consumptionItemsForm.removeAt(index);
  }

  private getConsumptionItemsValue(): ItemConsumption[] {
    return this.consumptionItemsForm
      .getRawValue()
      .filter((row) => row.isAvailable)
      .map(({ itemCode, quantity, uom }) => ({ itemCode, quantity, uom }));
  }

  saveConsumptions(): void {
    const cycle = this.activeCycle();
    if (!cycle) return;

    const items = this.getConsumptionItemsValue();
    this.isSavingConsumptions.set(true);

    this.dyeingCycleProxyService
      .updateItemConsumptions(cycle.id, items, this.waterUsageControl.value)
      .pipe(
        finalize(() => this.isSavingConsumptions.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ data }) => {
          if (data) {
            GlobalMessageUtil.success(
              this.localeHelperService.translateWithFallback('Success', 'Success'),
              this.localeHelperService.translateWithFallback('batchDyeingCycle.consumptionsSaved', 'Item consumptions saved'),
            );
            this.activeCycle.set(data);
            this.initConsumptionItems(data);
          }
        },
      });
  }

  confirmConsume(): void {
    this.confirm(
      this.localeHelperService.translateWithFallback(
        'batchDyeingCycle.confirmConsumeMessage',
        'Consume this cycle? This will finalize the item consumptions and cannot be undone.',
      ),
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmConsumeHeader', 'Confirm Consume'),
      () => this.consumeCycle(),
    );
  }

  private consumeCycle(): void {
    const cycle = this.activeCycle();
    if (!cycle) return;

    this.isConsuming.set(true);

    this.batchLotItemProxyService
      .consumeBatchCycle(this.batchId, { dyeingCycleId: cycle.id })
      .pipe(
        finalize(() => this.isConsuming.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.localeHelperService.translateWithFallback('Success', 'Success'),
            this.localeHelperService.translateWithFallback('batchDyeingCycle.cycleConsumed', 'Cycle consumed successfully'),
          );
          this.activeCycle.set({ ...cycle, consumptionsFinalized: true });
        },
      });
  }

  //#endregion

  //#region Shared helpers

  private confirm(message: string, header: string, onAccept: () => void): void {
    confirmAction(this.confirmationService, { message, header })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((accepted) => {
        if (accepted) onAccept();
      });
  }

  private executeCycleAction(
    action: Observable<ApiResponse<ProductionCardDyeingCycle>>,
    successMessage: string,
    onSuccess?: () => void,
  ): void {
    this.incrementLoading();
    action
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ data }) => {
          if (data) {
            GlobalMessageUtil.success(this.localeHelperService.translateWithFallback('Success', 'Success'), successMessage);
            this.activeCycle.set(data);
            this.initConsumptionItems(data);
            if (onSuccess) onSuccess();
          }
        },
      });
  }

  //#endregion

  //#region Start Cycle

  confirmStartCycle(): void {
    this.confirm(
      this.localeHelperService.translateWithFallback(
        'batchDyeingCycle.confirmStartMessage',
        `Start dyeing cycle for Batch ${this.batchNumber} on station ${DYEING_STATION_ASSET_ID}?`,
        { batchNumber: this.batchNumber, station: DYEING_STATION_ASSET_ID },
      ),
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmStartHeader', 'Confirm Start Cycle'),
      () => this.startCycle(),
    );
  }

  private startCycle(): void {
    const action = this.dyeingCycleProxyService.startCycleByBatch(this.batchId, DYEING_STATION_ASSET_ID);
    this.executeCycleAction(
      action,
      this.localeHelperService.translateWithFallback('batchDyeingCycle.cycleStarted', 'Cycle started successfully'),
    );
  }

  //#endregion

  //#region End Cycle

  confirmEndCycle(): void {
    this.confirm(
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmEndMessage', 'End the current dyeing cycle?'),
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmEndHeader', 'Confirm End Cycle'),
      () => this.endCycle(),
    );
  }

  private endCycle(): void {
    const cycle = this.activeCycle();
    if (!cycle) return;

    const stationId = cycle.startStationAssetId ?? DYEING_STATION_ASSET_ID;
    const action = this.dyeingCycleProxyService.endCycle(cycle.id, stationId);
    this.executeCycleAction(
      action,
      this.localeHelperService.translateWithFallback('batchDyeingCycle.cycleEnded', 'Cycle ended - Awaiting QA'),
    );
  }

  //#endregion

  //#region Quality Control

  confirmSubmitQA(): void {
    const approved = this.isApprovedControl.value;
    const comments = this.qaComments.value || '';

    if (!approved && !comments.trim()) {
      GlobalMessageUtil.error(
        this.localeHelperService.translateWithFallback('Error', 'Error'),
        this.localeHelperService.translateWithFallback('batchDyeingCycle.commentsRequired', 'Comments are required when rejecting'),
      );
      return;
    }

    const message = approved
      ? this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmApproveMessage', 'Approve this dyeing cycle?')
      : this.localeHelperService.translateWithFallback(
          'batchDyeingCycle.confirmRejectMessage',
          `Reject this dyeing cycle with comments: "${comments}"?`,
          { comments },
        );

    this.confirm(
      message,
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmQAHeader', 'Confirm Quality Control'),
      () => this.submitQualityControl(),
    );
  }

  private submitQualityControl(): void {
    const cycle = this.activeCycle();
    if (!cycle) return;

    const approved = this.isApprovedControl.value;
    const comments = this.qaComments.value || '';

    const action = this.dyeingCycleProxyService.qualityControlApproval(cycle.id, approved, comments || undefined);
    this.executeCycleAction(
      action,
      this.localeHelperService.translateWithFallback('batchDyeingCycle.qaSubmitted', 'Quality control submitted - Ready for Unloading'),
      () => {
        this.isApprovedControl.setValue(true);
        this.qaComments.setValue('');
      },
    );
  }

  //#endregion

  //#region Unload

  confirmUnload(): void {
    this.confirm(
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmUnloadMessage', 'Unload the dyeing machine?'),
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmUnloadHeader', 'Confirm Unload'),
      () => this.unloadCycle(),
    );
  }

  private unloadCycle(): void {
    const cycle = this.activeCycle();
    if (!cycle) return;

    const action = this.dyeingCycleProxyService.unloadCycle(cycle.id);
    this.executeCycleAction(
      action,
      this.localeHelperService.translateWithFallback('batchDyeingCycle.cycleUnloaded', 'Cycle unloaded successfully'),
      // Unloading a cycle with approved QC completes the batch; refresh it so the page behind the modal shows it right away.
      () => this.batchService.reloadBatch(this.batchId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(),
    );
  }

  //#endregion

  //#region Hard Approve / Hard Stop

  confirmHardApprove(): void {
    this.confirm(
      this.localeHelperService.translateWithFallback(
        'batchDyeingCycle.confirmHardApproveMessage',
        'Are you sure you want to hard approve this cycle? This will override the normal approval process.',
      ),
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmHardApproveHeader', 'Confirm Hard Approve'),
      () => this.hardApprove(),
    );
  }

  private hardApprove(): void {
    const cycle = this.activeCycle();
    if (!cycle) return;

    const action = this.dyeingCycleProxyService.hardApprove(cycle.id);
    this.executeCycleAction(
      action,
      this.localeHelperService.translateWithFallback('batchDyeingCycle.cycleHardApproved', 'Cycle hard approved successfully'),
    );
  }

  confirmHardStop(): void {
    this.confirm(
      this.localeHelperService.translateWithFallback(
        'batchDyeingCycle.confirmHardStopMessage',
        'Are you sure you want to hard stop this cycle? This will end the cycle and reject quality control.',
      ),
      this.localeHelperService.translateWithFallback('batchDyeingCycle.confirmHardStopHeader', 'Confirm Hard Stop'),
      () => this.hardStop(),
    );
  }

  private hardStop(): void {
    const cycle = this.activeCycle();
    if (!cycle) return;

    const action = this.dyeingCycleProxyService.hardStop(cycle.id);
    this.executeCycleAction(
      action,
      this.localeHelperService.translateWithFallback('batchDyeingCycle.cycleHardStopped', 'Cycle hard stopped successfully'),
    );
  }

  //#endregion

  onClose(): void {
    const cycle = this.activeCycle();
    if (cycle) {
      this.close({ action: 'save', data: cycle });
    } else {
      this.close({ action: 'close', data: null });
    }
  }
}
